<template>
  <div>
    <div v-if="loading" class="loading-mask">
      Loading...
    </div>

    <PageHeader :title="isSmallDevice?'':'面接一覧'" class="list">
      <template #title-header-content>
        <div class="flex-1 pl-2 flex justify-end items-center">

          <div class="bg-white flex text-white font-bold text-sm p-2 rounded ml-auto w-fit">
            <div class="w-28 rounded py-1 text-center cursor-pointer mr-2" :class="filter.title=='newGrad'?'bg-blue-700':'bg-gray-400'" @click="clickTitle('newGrad')">新卒</div>
            <div class="w-28 rounded py-1 text-center cursor-pointer mr-2" :class="filter.title=='mid'?'bg-blue-700':'bg-gray-400'" @click="clickTitle('mid')">中途</div>
            <div class="w-28 rounded py-1 text-center cursor-pointer mr-2" :class="filter.title=='cleaner'?'bg-blue-700':'bg-gray-400'" @click="clickTitle('cleaner')">清掃員</div>
          </div>

          <div class="mx-5">
            <PrimaryButton text="検索" @click="getData()" class="min-w-100px" :buttonContents="'要'" />
          </div>

          <PrimaryButton v-if="regularStaffAuth" class="bg-green-100 text-green-700 mr-2 block" text="新卒集計" @click="openList()">
            <Icon iconName="ViewGrid" slot="before" class="h-4 w-4" />
          </PrimaryButton>

          <div class="mx-1">
            <PrimaryButton text="クリア" :size="isSmallDevice?'xs':'normal'" class="off px-1" @click="clear" :buttonContents="'要'" />
          </div>

          <div class="ml-2 font-bold text-white">
            {{ records }} 人
          </div>

          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{'animate-spin': loading}"
            class="text-white hover:text-gray-300 active:text-gray-400 ml-4 cursor-pointer self-center"
            @click.native="getData"
          />
        </div>
      </template>
      <template #page-header-content>
        <div class="flex flex-1 justify-end items-end">

          <!-- 採否検索 -->
          <select
            v-model="filter.res"
            class="text-sm border-gray-300 rounded text-gray-700 mx-2 w-32"
            :class="filter.res&&filter.res!='採否全て'?'bg-yellow-200 font-bold':''"
          >
            <option
              v-for="(v, i) in ['採否全て', '採用', '不採用', '未定', '辞退']" :key="'f'+i"
              :value="v"
              class="">
              {{ v }}
            </option>
          </select>

          <!-- 性別検索 -->
          <select
            v-model="filter.sex"
            class="text-sm border-gray-300 rounded text-gray-700 mx-2 w-32"
            :class="filter.sex&&filter.sex!='男女'?'bg-yellow-200 font-bold':''"
          >
            <option
              v-for="(v, i) in ['男女', '男性', '女性']" :key="'2f'+i"
              :value="v"
              class="">
              {{ v }}
            </option>
          </select>

          <!-- 対象者検索 -->
          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="filter.name"
              class="title w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="filter.name?'bg-yellow-200 font-bold':''"
              placeholder="対象者名"
            />
          </div>

          <!-- 面接官検索 -->
          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="filter.interviewer"
              class="title min-w-200 py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="filter.interviewer?'bg-yellow-200 font-bold':''"
              placeholder="面接官"
            />
          </div>

          <!-- 部署検索 -->
          <select
            v-model="filter.dep"
            class="text-sm border-gray-300 rounded text-gray-700 mx-2 w-40"
            :class="filter.dep?'bg-yellow-200 font-bold':''"
          >
            <option
              v-for="d in [{ departmentId: '', name: '部署全て' }].concat(toDepartmentList)" :key="'3f'+d.departmentId"
              :value="d.departmentId">
              {{ d.name }}
            </option>
          </select>

          <!-- ステータス検索 -->
          <select
            v-model="filter.status"
            class="text-sm border-gray-300 rounded text-gray-700 mx-2 w-32"
            :class="filter.status&&filter.status!='ステータス全て'?'bg-yellow-200 font-bold':''"
          >
            <option
              v-for="(v, i) in ['ステータス全て', '完了済み', '未完了']" :key="'2f'+i"
              :value="v"
              class="">
              {{ v }}
            </option>
          </select>

          <div class="-mt-2 ml-2">
            <div class="text-xs font-bold text-white">メインタスク作成日</div>
            <div class="flex">
              <!-- 開始日 -->
              <WmsDateInput
                name="start"
                v-model="filter.start"
                class="w-32"
                :class="filter.start?'yellow':''"
              />

              <div class="font-bold text-white mx-2 self-center">～</div>

              <!-- 終了日 -->
              <WmsDateInput
                name="end"
                v-model="filter.end"
                class="w-32"
                :class="filter.end?'yellow':''"
              />
            </div>
          </div>
        </div>
      </template>
    </PageHeader>

    <main class="absolute w-full bottom-4 top-32">
      <div class="mx-auto px-2 sm:px-4 lg:px-8 main">
        <div class="bg-white rounded-md border border-gray-200 ---overflow-auto h-full">

          <div v-if="list && list.length" class="h-full overflow-y-auto">

            <table class="w-full overflow-auto max-table">
              <thead class="sticky top-0 t-thead">
                <tr v-if="list && list.length" class="">
                  <th v-for="(col, idx) in columns" :key="'c'+idx" scope="col" class="p-2 text-xs text-gray-600 tracking-wider whitespace-pre-line text-left" :class="col.class">
                    {{ col.title }}
                  </th>
                </tr>
              </thead>

              <tr v-for="(row, idx) in list" :key="'l'+idx" class="tracking-wider text-sm hover-color cursor-pointer text-gray-700 border-b " @dblclick="openMainTab(row)">

                <!-- 採否 -->
                <td class="row text-center">
                  <div class="white-space-nowrap px-2 py-1 rounded font-bold text-white res" :style="row.resColor">{{ row.res }}</div>
                </td>
                <!-- 対象者・性別 -->
                <td class="row">
                  <div class="truncate w-32 font-bold">{{ row.interviewsName }}</div>
                  <div v-if="row.sex" class="truncate w-32 text-xs mt-1 font-bold" :style="row.sexColor">（{{ row.sex }}）</div>
                </td>
                <!-- 面接官・担当者 -->
                <td class="row text-xs">
                  <div v-if="row.interviewsRegularStaff" class="truncate w-28">{{ row.interviewer }}</div>
                  <div class="truncate w-28">{{ row.toStaffName }}</div>
                </td>
                <!-- 希望部署 -->
                <td class="row">
                  <select
                    v-if="row.interviewsRegularStaff&&row.interviewsDepartmentId"
                    v-model="row.interviewsDepartmentId[0]"
                    class="text-sm border-0 text-gray-700 select"
                    :disabled="true"
                  >
                    <option
                      v-for="d in toDepartmentList" :key="idx+'d'+d.departmentId"
                      :value="d.departmentId"
                      class="">
                      {{ d.name }}
                    </option>
                  </select>
                  <!-- 物件名 -->
                  <div v-else-if="!row.interviewsRegularStaff">
                    {{ row.interviewsAppliedWorkLocationAddress }}
                  </div>
                </td>
                <!-- ステータス -->
                <td class="row text-center">
                  <div class="white-space-nowrap font-bold text-xs" >{{ row.statusText }}</div>
                </td>
                <!-- 開く -->
                <td class="row text-center">
                  <div @click="openMainTab(row)" class="blue-text whitespace-nowrap">メイン確認</div>
                </td>
                <!-- サブタスク -->
                <td class="row text-center">
                  <div @click="openSubTab(row)" class="blue-text whitespace-nowrap">サブ確認</div>
                </td>
              </tr>
            </table>
          </div>

          <!-- <div class="text-transparent text-xs">{{reload}}</div> -->

          <div v-if="!loading && list.length === 0" class="w-full h-full flex justify-center items-center text-gray-400">
            <EmptyMessage
              iconName="Document"
              message="該当する案件はありません。"
            />
          </div>
          
          <div v-else-if="!loading&&list&&list.length" class="w-fit mx-auto mt-3">
            <pagination id="pagenation" v-model="page" :per-page="perPage" :records="records" class="text-center" @paginate="getData" />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PageHeader from '@components/PageHeader.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WmsDateInput from '@wmscomponents/WmsDateInput.vue'
import EmptyMessage from '@components/EmptyMessage.vue'
import Pagination from 'vue-pagination-2'
import * as interviewsListManager from '@managers/interviewsListManager'
import * as utils from '@libs/utils'
import * as moment from 'moment'
import * as dialogs from '@libs/dialogs'

import PageModel from '../../main/pages/PageModel.vue'

const sess = 'sessionInterviews'

const col1 = [
        { title: '採否' },
        { title: '対象者\n性別' },
        { title: '面接官\n担当者' },
        { title: '希望部署' },
        { title: 'ステータス', class: 'text-center' },
        { title: '' },
        { title: '' },
      ]
const col2 = [
        { title: '採否' },
        { title: '対象者\n性別' },
        { title: '担当者' },
        { title: '物件名' },
        { title: 'ステータス', class: 'text-center' },
        { title: '' },
        { title: '' },
      ]

export default {
  extends: PageModel,
  components: {
    Icon,
    PrimaryButton,
    WmsDateInput,
    PageHeader,
    EmptyMessage,
    Pagination
  },

  data() {
    return {
      // 一覧データ
      list: [],
      loading: false,
      filter: {
        title: 'newGrad',
        res: '採否全て',
        sex: '男女',
        dep: '',
        status: 'ステータス全て',
        start: moment().subtract(4, 'months').startOf('month').format('YYYY-MM-DD'),
        end: moment().endOf('month').format('YYYY-MM-DD')
      },
      // 部署リスト
      toDepartmentList: [],
      // カラム
      columns: [],
      // ページ数
      page: 1,
      // １ページの最大件数
      perPage: 50,
      // 全件数
      records: 0,
    }
  },

  computed: {

    /**
     * 権限
     */
    auth() {
      return this.$store.getters.auth
    },

    /**
     * 管理者権限あり
     */
    admin() {
      return utils.admin(this.auth)
    },

    /**
     * 社員面接権限あり
     */
    regularStaffAuth() {
      if (this.auth && this.auth.regularStaffAuth) {
        return true
      }
      return false
    },

    isSmallDevice() {
      return (utils.deviceInfo.isMobile || utils.deviceInfo.isTablet)
    },
  },

  created() {
    this.toDepartmentList = this.$store.getters.departmentList
    this.loadCondition()
    if (!this.regularStaffAuth) {
      this.filter.title = 'cleaner'
    }
    this.getData()
  },

  methods: {
    /**
     * データ取得
     */
    async getData(num) {
      this.loading = true
      this.setSessionStorage()
      if (num) {
        this.page = num
      } else {
        this.page = 1
      }
      if (this.filter.title === 'cleaner') {
        this.columns = utils.clone(col2)
      } else {
        this.columns = utils.clone(col1)
      }
      this.list = []
      const res = await interviewsListManager.getList(this, this.filter, this.perPage, this.page)
      if (res.list && res.list.length) {
        this.list = res.list
        this.records = res.total
      } else {
        this.list = []
        this.records = 0
      }
      this.loading = false
    },

    /**
     * 画面切り替え
     */
    async clickTitle(val) {
      if (this.loading) {
        return
      }
      if (!this.regularStaffAuth) {
        await dialogs.showErrorDialog('社員面接権限無し', '社員の面接はご確認できません。')
        this.filter.title = 'cleaner'
        return
      }
      this.filter.title = val
      this.getData()
    },

    /**
     * メインタスク開く
     * @param row
     */
    openMainTab(row) {
      const route = this.$router.resolve({
        name: 'RequestPost',
        query: { mode: 1, requestPostId: row._id }
      })

      window.open(route.href, '_blank')
    },

    /**
     * サブタスク開く
     * @param row
     */
    openSubTab(row) {
      const route = this.$router.resolve({
        name: 'TaskRequest',
        query: { requestPostId: row._id }
      })

      window.open(route.href, '_blank')
    },

    /**
     * 検索条件をクリア
     */
    clear() {
      this.removeSettion()
      this.$router.go({path: this.$router.currentRoute.path, force: true})
    },

    /**
     * セッションストレージ削除
     */
    removeSettion() {
      sessionStorage.removeItem(sess)
    },

    /**
     * 検索条件をセッションストレージへ保存
     */
    setSessionStorage() {
      const searchStr = JSON.stringify(this.filter)
      sessionStorage.setItem(sess, searchStr)
    },

    /**
     * セッションストレージから検索条件を取得
     */
    loadCondition() {
      const s = sessionStorage.getItem(sess)
      if (s) {
        this.filter = JSON.parse(s)
      }
    },
    
    /**
     *  収益表示
     */
    openList() {
      const route = this.$router.resolve({
        name: 'NewGradList'
      })

      window.open(route.href, '_blank')
    },
  },
}
</script>

<style scoped>
  .list {
    min-width: 1380px;
  }

  .main {
    height: calc(100% - 67px);
    min-width: 1370px;
  }

  .t-thead {
    z-index: 5;
    background: #ebf0fb;
  }

  .row {
    padding: 8px;
  }

  .res {
    max-width: 200px;
  }

  .blue-text {
    color: blue;
    font-weight: bold;
  }

  .hover-color:hover {
    background: rgba(225, 244, 247, 0.67) !important;
  }

  .yellow >>> input[type=text] {
    background-color: #efdf8a !important;
    font-weight: bold !important;
  }

  .select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    color: #000;
    opacity: 1;
  }

  /* ページネーション */
  #pagenation >>> ul {
    display: flex !important;
    color: rgb(6, 125, 194);
    font-weight: bold !important;
    cursor: pointer;
  }

  #pagenation >>> ul > li {
    padding: 0 10px;
  }

  #pagenation >>> ul > .active {
    background: rgb(6, 125, 194);
    color: white;
  }

  #pagenation >>> p {
    color: gray;
    font-size: small;
}
</style>